import type { MediaAsset, Playlist, PlaylistItem } from '@ghostscreen/contracts';
import { formatList } from '../../i18n/format';
import { i18n } from '../../i18n/i18n';

export type PlaylistListRowModel = {
  playlist: Playlist;
  name: string;
  meta: string;
  screensLabel: string;
  durationLabel: string;
};

export function formatPlaylistLoopDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '—';
  const total = Math.round(totalSeconds);
  if (total < 60) return i18n.t('playlists:duration.seconds', { seconds: total });
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return seconds
    ? i18n.t('playlists:duration.minutesSeconds', { minutes, seconds })
    : i18n.t('playlists:duration.minutes', { minutes });
}

export function buildPlaylistListRow(input: {
  playlist: Playlist;
  items: PlaylistItem[];
  mediaById: ReadonlyMap<string, MediaAsset>;
  screenCount: number;
}): PlaylistListRowModel {
  const { playlist, items, mediaById, screenCount } = input;
  const names = items
    .slice(0, 3)
    .map((item) => mediaById.get(item.mediaAssetId)?.originalFilename)
    .filter((name): name is string => Boolean(name));
  const totalSeconds = items.reduce((sum, item) => sum + item.durationSeconds, 0);

  return {
    playlist,
    name: playlist.name || i18n.t('playlists:untitled'),
    meta: items.length
      ? i18n.t('playlists:row.meta', { count: items.length, names: formatList(names, 'unit') })
      : i18n.t('playlists:row.empty'),
    screensLabel: screenCount
      ? i18n.t('playlists:row.onScreens', { count: screenCount })
      : i18n.t('playlists:row.notOnScreen'),
    durationLabel: items.length
      ? i18n.t('playlists:row.loop', { duration: formatPlaylistLoopDuration(totalSeconds) })
      : '—',
  };
}

export function playlistDrawerSummary(items: PlaylistItem[]): string {
  if (!items.length) return i18n.t('playlists:summary.empty');
  const total = items.reduce((sum, item) => sum + item.durationSeconds, 0);
  return i18n.t('playlists:summary.items', {
    count: items.length,
    duration: formatPlaylistLoopDuration(total),
  });
}

/** Insert a freshly created playlist into the list cache so the drawer can open immediately. */
export function mergeCreatedPlaylist(
  existing: Playlist[] | undefined,
  created: Playlist,
): Playlist[] {
  const list = existing ?? [];
  if (list.some((playlist) => playlist.id === created.id)) return list;
  return [...list, created];
}
